import MemberButton from 'components/common/buttons/MemberButton';
import { deleteBiometricData, getBiometricData, setUserBiometricData } from 'functions/webUtils';
import I18n from 'hooks/useI18n';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  onCloseConfirmModal,
  toggleCreateNewPasswordModal,
  toggleVerifyPasswordModal,
} from 'redhotpie-shared-lib/actions/commonStateAction';
import { ACCOUNT_STATUS, FaceIDDelayTooltip, FaceIDShowTooltip, SESSIONS } from 'redhotpie-shared-lib/constants/constants';
import { storageHelper } from 'redhotpie-shared-lib/functions';
import { handleShowConfirmModal } from 'redhotpie-shared-lib/functions/commonUtils';
import { storageCachingSupported } from 'redhotpie-shared-lib/functions/storageHelper';
import { useDispatch, useSelector } from 'redhotpie-shared-lib/hooks/useRedux';
import { IUserLoginInfo } from 'redhotpie-shared-lib/interfaces/IUser';
import { getUserInfo } from 'redhotpie-shared-lib/selectors/loginSelector';

import { createUserToolTip } from 'redhotpie-shared-lib/actions/userActions';
import { getStore } from 'redhotpie-shared-lib/store/myStore';

interface BiometricProviderProps {
  children: React.ReactNode;
}

export const LOGIN_PATHS = ['/rjsapp', '/rjsapp/', '/rjsapp/login', '/rjsapp/login/'];

type AskEnableBiometricObject = {
  [key: string]: number; // key is username in lowercase, value is date in timestamp
};

export type SECURE_BIOMETRIC_LEVEL = 'Moderate' | 'High';

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

interface BiometricContextProps {
  isBiometricEnabled: boolean;
  isBiometricSupported: boolean;
  registerBiometric: (password: string, userInfo?: Pick<IUserLoginInfo, 'displayName' | 'userId'>) => Promise<boolean>;
  deleteBiometric: () => void;
  checkIsBiometricEnabled: (displayName?: string) => boolean;
  setSecureLevelBiometric: (level: SECURE_BIOMETRIC_LEVEL) => void;
  secureLevel: SECURE_BIOMETRIC_LEVEL;
  processBiometricAuthentication: (
    displayName: string,
    onSuccess: (displayName: string, password: string) => void,
    onError?: (err: any) => void
  ) => void;
  askEnableBiometric: (
    userInfo: Pick<IUserLoginInfo, 'displayName' | 'userId' | 'newPasswordRequired'>,
    password: string,
    verifyPassword?: boolean,
    callback?: (credential?: PublicKeyCredential) => void,
    isRegister?: boolean
  ) => void;
  getCredential: (userInfo: Pick<IUserLoginInfo, 'displayName' | 'userId'>) => Promise<PublicKeyCredential | undefined>;
}

export type BiometricUser = {
  displayName: string;
  password: string;
  credentialId: string;
  avatarUrl: string;
  secureLevel: SECURE_BIOMETRIC_LEVEL;
};

export type BiometricData = {
  [key: string]: BiometricUser;
};

const BiometricContext = createContext<BiometricContextProps>({
  isBiometricEnabled: false,
  isBiometricSupported: false,
  registerBiometric: () => Promise.resolve(false),
  deleteBiometric: () => {},
  checkIsBiometricEnabled: () => false,
  setSecureLevelBiometric: () => {},
  secureLevel: 'Moderate',
  processBiometricAuthentication: () => {},
  askEnableBiometric: () => {},
  getCredential: () => Promise.resolve(undefined),
});

// Helper function to convert ArrayBuffer to base64
export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const BiometricProvider = ({ children }: BiometricProviderProps) => {
  const [isBiometricEnabled, setIsBiometricEnabled] = useState(false);
  const [secureLevel, setSecureLevel] = useState<SECURE_BIOMETRIC_LEVEL>('Moderate');
  const [isBiometricSupported, setIsBiometricSupported] = useState(false);
  const userInfoSelector = useSelector(getUserInfo);
  const userInfo = useMemo(() => userInfoSelector.asMutable(), [userInfoSelector]);
  const dispatch = useDispatch();
  // Helper function to convert base64 to ArrayBuffer
  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const checkIsBiometricEnabled = (displayName?: string) => {
    const biometricData = getBiometricData() as BiometricData;
    const lastedLoggedUserName = storageHelper.getKey(SESSIONS.LASTED_LOGGED_USER_NAME)?.toLowerCase() || '';
    setIsBiometricEnabled(!!biometricData[displayName || lastedLoggedUserName]);
    return !!biometricData[displayName || lastedLoggedUserName];
  };

  useEffect(() => {
    window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable().then(isBiometricSupported => {
      setIsBiometricSupported(isBiometricSupported && storageCachingSupported());
      checkIsBiometricEnabled();
      const securityLevel = storageHelper.getKey(SESSIONS.SECURITY_BIOMETRIC_LEVEL);
      if (securityLevel) {
        setSecureLevel(securityLevel as SECURE_BIOMETRIC_LEVEL);
      }
    });
  }, []);

  const deleteBiometric = () => {
    try {
      deleteBiometricData(userInfo.displayName);
      setIsBiometricEnabled(false);
    } catch (error: any) {
      console.error('Error during biometric registration:', error);
    }
  };

  const setSecureLevelBiometric = (level: SECURE_BIOMETRIC_LEVEL) => {
    setSecureLevel(level);

    // handle set security level into localStorage here
    storageHelper.setKey(SESSIONS.SECURITY_BIOMETRIC_LEVEL, level);
  };

  const getCredential = async (userInfo: Pick<IUserLoginInfo, 'userId'>) => {
    if (!userInfo || !userInfo.userId) return undefined;
    const largeNumber = userInfo.userId as number;
    const userIdBuffer = new ArrayBuffer(8);
    const userIdView = new DataView(userIdBuffer);
    userIdView.setBigInt64(0, BigInt(largeNumber), true);

    const userIdBytes = new Uint8Array(userIdBuffer);
    const publicKey: PublicKeyCredentialCreationOptions = {
      rp: {
        name: 'RedHotPie',
        id: window.location.hostname, // Relying Party ID (your domain)
      },
      user: {
        id: userIdBytes, // Replace with a unique user ID (not secure without backend)
        name: 'RedHotPie', // Replace with the user\'s display name
        displayName: 'RedHotPie',
      },
      challenge: new Uint8Array(16), // Insecure: Static or client-generated challenge
      pubKeyCredParams: [
        { type: 'public-key' as const, alg: -7 }, // ES256 algorithm
        { type: 'public-key' as const, alg: -257 }, // RS256 algorithm
      ],
      timeout: 60000, // 60 seconds
      attestation: 'direct', // No attestation for simplicity (less secure)

      // --- Optional Configurations ---
      authenticatorSelection: {
        // requireResidentKey: true, // Whether a resident key (discoverable credential) is required
        // residentKey: 'required', // 'discouraged', 'preferred', or 'required'
        userVerification: 'required', // 'required', 'preferred', or 'discouraged' for user verification (e.g., fingerprint, PIN)
        authenticatorAttachment: 'platform', // 'platform' (built-in) or 'cross-platform' (e.g., security key)
      },
    };

    const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential;
    return credential;
  };

  const registerBiometric = async (password: string, _userInfo?: Pick<IUserLoginInfo, 'displayName' | 'userId' | 'avatarUrl'>) => {
    if (!isBiometricSupported) {
      return false;
    }
    const userInfoData = _userInfo || userInfo;
    try {
      const credential = await getCredential(userInfoData);

      console.log('credential', credential);

      if (credential) {
        // validateUserDataService.validateUserPassword()
        const { displayName } = userInfoData;
        const { avatarUrl } = userInfo;
        console.log('Biometric registration successful:', credential);
        setUserBiometricData(
          {
            credentialId: arrayBufferToBase64(credential.rawId),
            displayName,
            avatarUrl,
            password,
            secureLevel: 'Moderate',
          },
          displayName
        );
        setIsBiometricEnabled(checkIsBiometricEnabled());
        return true;
      } else {
        console.log('Biometric registration failed.');
        return false;
      }
    } catch (error: any) {
      console.error('Error during biometric registration:', error);
      return false;
    }
  };

  const processBiometricAuthentication = async (
    displayName: string,
    onSuccess: (displayName: string, password: string, fromBiometric?: boolean) => void,
    onError?: (err: any) => void
  ) => {
    const biometricData = getBiometricData() as BiometricData;
    const userBiometricData = biometricData[displayName];
    if (!userBiometricData) {
      console.error('No biometric credential found. Please register first (insecurely).');
      onError?.('No biometric credential found. Please register first (insecurely).');
      return;
    }

    const credentialIdBase64 = userBiometricData.credentialId;

    if (!credentialIdBase64) {
      console.error('No biometric credential found. Please register first (insecurely).');
      onError?.('No biometric credential found. Please register first (insecurely).');
      return;
    }

    try {
      const credentialId = base64ToArrayBuffer(credentialIdBase64);

      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: new Uint8Array(16), // Insecure: Static or client-generated challenge
          timeout: 60000, // 60 seconds
          rpId: window.location.hostname,
          allowCredentials: [
            {
              id: new Uint8Array(credentialId), // Pass as ArrayBuffer
              type: 'public-key',
            },
          ],
        },
      });

      if (assertion) {
        console.log('Biometric authentication successful:', assertion);
        console.error('Biometric authentication successful (insecurely). Check console.');
        onSuccess(displayName, userBiometricData.password, true);
      } else {
        console.log('Biometric authentication failed.');
        console.error('Biometric authentication failed.');
        onError?.('Biometric authentication failed.');
      }
    } catch (error: any) {
      console.error(`Error during biometric authentication: ${error.message || 'An unknown error occurred'}`);
      onError?.(error.message);
    }
  };

  const getUserSettingFromStore = () => {
    return getStore().getState().loginReducer.userInfo;
  };

  const setDateAskedEnableBiometric = () => {
    const { toolTips } = getUserSettingFromStore();
    const item = toolTips.find((itm: any) => FaceIDDelayTooltip === itm.name);
    if (item && item.ViewCount === 0) {
      dispatch(createUserToolTip(item.id));
    }
  };

  const setDontAskBiometricAgain = () => {
    const { toolTips } = getUserSettingFromStore();
    const item = toolTips.find((itm: any) => FaceIDShowTooltip === itm.name);
    if (item && item.ViewCount === 0) {
      dispatch(createUserToolTip(item.id));
    }
  };

  const askEnableBiometric = (
    userInfo: Pick<IUserLoginInfo, 'displayName' | 'userId' | 'newPasswordRequired'>,
    password: string,
    verifyPassword?: boolean,
    callback?: (data?: any) => void,
    isRegister?: boolean
  ) => {
    const { newPasswordRequired, displayName } = userInfo;
    if (!displayName) return;

    if (!isBiometricSupported) {
      console.error('Biometric is not supported!');
      return;
    }

    if (checkIsBiometricEnabled(displayName)) {
      console.log('Biometric is enabled!');
      return;
    }

    const { toolTips, accountStatus } = getUserSettingFromStore();
    const isShowAuthenticationModal = accountStatus === ACCOUNT_STATUS.AUTHENTICATION_REQUIRE;
    const itemTooltip = toolTips.find((itm: any) => FaceIDShowTooltip === itm.name);
    const showTooltip = (!isShowAuthenticationModal && itemTooltip && itemTooltip.display) || false;

    const shouldShowModal = showTooltip;

    if (shouldShowModal) {
      handleShowConfirmModal({
        // isOverlayBlack: true,
        headerText: I18n.t('BiometricRequire.Title'),
        messageConfirm: I18n.t('BiometricRequire.WouldYouLikeToEnableFaceIDTouchIDForFasterAndMoreSecureLogins'),
        okText: I18n.t('Btn.EnableFaceID/TouchID'),
        okAction: () => {
          if (isRegister) {
            // Logic enable faceID / touchID for Register
            redirectToRegister(userInfo, callback);
          } else if (newPasswordRequired) {
            // open modal ask to create password
            handleShowConfirmModal({
              headerText: I18n.t('BiometricRequire.Title'),
              messageConfirm: I18n.t('BiometricRequire.PasswordRequire'),
              okText: I18n.t('Btn.CreatePassword'),
              okAction: () => {
                dispatch(
                  toggleCreateNewPasswordModal({
                    isShowModal: true,
                    callbackCreatePasswordSuccess: (passwordCreated: string) => {
                      registerBiometric(passwordCreated, userInfo);
                      typeof callback === 'function' && callback();
                    },
                    callbackCreatePasswordFailed: () => {
                      typeof callback === 'function' && callback();
                    },
                    des: I18n.t('CreateNewPassword.PleaseCreateYourPasswordToEnableFaceIDTouchID'),
                  })
                );
              },
              cancelText: I18n.t('Btn.NeverMind'),
              cancelAction: () => {
                typeof callback === 'function' && callback();
              },
            });
          } else if (verifyPassword) {
            // should open modal ask to verify password
            // this case used for app change state from background to foreground
            dispatch(
              toggleVerifyPasswordModal({
                isShowModal: true,
                callbackVerifyPasswordSuccess: (passwordVerified: string) => {
                  registerBiometric(passwordVerified, userInfo);
                  typeof callback === 'function' && callback();
                },
              })
            );
          } else {
            registerBiometric(password, userInfo);
            typeof callback === 'function' && callback();
          }
        },
        cancelText: I18n.t('Btn.MaybeLater'),
        cancelAction: () => {
          setDateAskedEnableBiometric();
          if (!isRegister) {
            typeof callback === 'function' && callback();
          }
        },
        extraFooter: (
          <div className="biome-footer">
            <MemberButton
              text={I18n.t('BiometricRequire.DontShowMeThisAgain')}
              onClick={() => {
                dispatch(onCloseConfirmModal());
                setDontAskBiometricAgain();
                if (!isRegister) {
                  typeof callback === 'function' && callback();
                }
              }}
              color="text"
            />
          </div>
        ),
        disableClickOverlay: true,
        disableEscClose: true,
      });
    } else {
      if (isRegister) {
        // Logic enable faceID / touchID for Register
        redirectToRegister(userInfo, callback);
      } else {
        typeof callback === 'function' && callback();
      }
    }
  };

  const redirectToRegister = async (
    userInfo: Pick<IUserLoginInfo, 'displayName' | 'userId' | 'newPasswordRequired'>,
    callback?: (data?: any) => void
  ) => {
    // Logic enable faceID / touchID for Register
    const credential = await getCredential(userInfo);
    // console.log('credential', credential);
    callback?.(credential);
  };

  return (
    <BiometricContext.Provider
      value={{
        isBiometricEnabled,
        isBiometricSupported,
        registerBiometric,
        deleteBiometric,
        checkIsBiometricEnabled,
        setSecureLevelBiometric,
        secureLevel,
        processBiometricAuthentication,
        askEnableBiometric,
        getCredential,
      }}>
      {children}
    </BiometricContext.Provider>
  );
};

export default BiometricProvider;

export const useBiometricContext = () => useContext(BiometricContext);
